import {
  type CSSProperties,
  type FC,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createRoot } from 'react-dom/client';
import { type LauncherConfig, resolveLauncherConfig } from './config.js';
import { getContent } from './i18n/content.js';
import {
  deriveOrigin,
  type OpenVideoMessage,
  parseWidgetMessage,
  postWidgetMessage,
} from './messages.js';
import { launcherCss } from './theme/styles.js';
import { tokens } from './theme/tokens.js';

/**
 * The host-header "Ask" launcher (Story 4.4) — the HOST side of the postMessage
 * handshake. It renders a warm orange→red gradient pill ("✦ Ask") that sits
 * AMONG the host header actions (never a floating FAB — the deliberate
 * difference from the SkillRx bot on shared surfaces) and toggles a right/left
 * docked iframe panel over the page.
 *
 * Isolation / lazy-load (AC 4 / NFR11): the `<iframe>` is created ONLY on the
 * first "Ask" click, not at page load, so the launcher's only page-load cost is
 * the button + a click handler. The iframe isolates all widget CSS/JS/perf from
 * the host.
 *
 * Handshake (AC 2): on a valid `READY` from the iframe's `contentWindow`+origin,
 * the launcher posts `AUTH` with the configured tenant/locale/anonUserId to the
 * pinned widget origin (never `'*'`). It never acts on a message
 * `parseWidgetMessage` rejected.
 *
 * Locale follows the host (REQ-028): when the `locale` prop changes after the
 * handshake, AUTH is re-sent with the new value. The widget applies a later AUTH
 * as a re-render, not a remount, so the conversation survives — existing answers
 * stay in the language they were written in, and only new turns switch.
 */
export interface AskLauncherProps extends LauncherConfig {
  /** Render the compact icon-only ✦ variant (narrow headers). Defaults to full "✦ Ask". */
  compact?: boolean;
}

/**
 * Resolve an absolute widget URL (relative paths are resolved against the host
 * origin) and append the `?host=<encoded host origin>` param the widget uses to
 * pin the allowed host origin. Returns the absolute src + the widget origin to
 * pin host-side. Kept pure/injectable so it is unit-testable without a DOM.
 */
export function buildWidgetSrc(
  widgetUrl: string,
  hostOrigin: string,
): { src: string; widgetOrigin: string } {
  const absolute = new URL(widgetUrl, hostOrigin);
  absolute.searchParams.set('host', hostOrigin);
  return { src: absolute.toString(), widgetOrigin: absolute.origin };
}

/**
 * Build the full-screen player URL for a validated OPEN_VIDEO payload. ONLY Vimeo
 * is embeddable — via `player.vimeo.com` — and only for the pinned `videoId`
 * (never the raw `url`). Autoplay is on; `startMs` becomes the Vimeo deep-link
 * offset (`#t=<s>s`). Pure/injectable.
 */
export function buildPlayerSrc(payload: OpenVideoMessage['payload']): string {
  const startSeconds =
    payload.startMs !== undefined && payload.startMs > 0 ? Math.floor(payload.startMs / 1000) : 0;
  const id = encodeURIComponent(payload.videoId);
  // vimeo → the only embeddable provider (the KB is Vimeo-only).
  const base = `https://player.vimeo.com/video/${id}?autoplay=1`;
  return startSeconds > 0 ? `${base}#t=${startSeconds}s` : base;
}

export const AskLauncher: FC<AskLauncherProps> = (props) => {
  const { compact = false, ...config } = props;
  const resolved = resolveLauncherConfig(config);
  const content = getContent(resolved.locale ?? 'en');

  const [open, setOpen] = useState(resolved.defaultOpen);
  // Lazy-load latch: the iframe is not rendered until the first "Ask" click —
  // or immediately when `defaultOpen` (a host deep-link opened us pre-expanded).
  const [mounted, setMounted] = useState(resolved.defaultOpen);
  // Host-page video lightbox (Interaction 2): the validated OPEN_VIDEO payload
  // currently playing full-screen, or null when closed.
  const [video, setVideo] = useState<OpenVideoMessage['payload'] | null>(null);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Set on the widget's READY. AUTH is only meaningful once the widget is
  // listening, so a locale change before READY is simply carried by the first AUTH.
  const widgetReadyRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const lightboxCloseRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();

  const hostOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  // Prefer the derived origin from an absolute widgetUrl; for a same-origin
  // RELATIVE widgetUrl `resolveLauncherConfig` yields '' → resolve against the
  // host origin. Either way the pinned origin is an EXACT origin, never '*'.
  const { src: widgetSrc, widgetOrigin } =
    hostOrigin.length > 0
      ? buildWidgetSrc(resolved.widgetUrl, hostOrigin)
      : { src: resolved.widgetUrl, widgetOrigin: resolved.allowedWidgetOrigin };
  const allowedWidgetOrigin =
    resolved.allowedWidgetOrigin.length > 0
      ? resolved.allowedWidgetOrigin
      : widgetOrigin.length > 0
        ? widgetOrigin
        : deriveOrigin(widgetSrc);

  // Host → widget session bootstrap. Sent on READY, and again whenever the
  // locale changes after the handshake.
  const postAuth = useCallback(() => {
    postWidgetMessage(
      iframeRef.current?.contentWindow,
      {
        source: 'bln-support-bot',
        v: 1,
        type: 'AUTH',
        payload: {
          tenant: resolved.tenant,
          ...(resolved.locale !== undefined ? { locale: resolved.locale } : {}),
          ...(resolved.anonUserId !== undefined ? { anonUserId: resolved.anonUserId } : {}),
          brandName: resolved.brandName,
          ...(resolved.hostNav ? { hostNav: true } : {}),
        },
      },
      allowedWidgetOrigin,
    );
  }, [
    allowedWidgetOrigin,
    resolved.tenant,
    resolved.locale,
    resolved.anonUserId,
    resolved.brandName,
    resolved.hostNav,
  ]);

  const close = useCallback(() => {
    setOpen(false);
    // Return focus to the opener (WCAG 2.1 AA — front-end-spec.md#Accessibility).
    buttonRef.current?.focus();
  }, []);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev) {
        setMounted(true);
        return true;
      }
      // Closing via the button — focus stays on the button (already focused).
      return false;
    });
  }, []);

  // Host side of the handshake: listen for messages from the pinned widget
  // origin + the iframe's own contentWindow. Anything `parseWidgetMessage`
  // rejects is ignored (fail-closed). READY → post AUTH; RESIZE(height<=0) → the
  // widget's ✕/Escape close signal (a positive height is intentionally IGNORED —
  // see the RESIZE branch / FIX B).
  useEffect(() => {
    if (!mounted) {
      return;
    }
    function onMessage(event: MessageEvent): void {
      const message = parseWidgetMessage(event, {
        origin: allowedWidgetOrigin,
        source: iframeRef.current?.contentWindow ?? null,
      });
      if (message === null) {
        return;
      }
      if (message.type === 'READY') {
        widgetReadyRef.current = true;
        postAuth();
      } else if (message.type === 'RESIZE') {
        // The panel is a FIXED-height, viewport-bounded, bottom-anchored panel
        // whose height is governed ENTIRELY by CSS (`.bln-ask-overlay` height +
        // the mobile full-screen @media branch). We deliberately DO NOT size the
        // wrapper to the widget's reported content height — the conversation
        // scrolls INSIDE the fixed panel (FIX B). RESIZE survives only as the
        // ✕/Escape close signal (height <= 0); a positive height is a no-op.
        if (message.payload.height <= 0) {
          close();
        }
      } else if (message.type === 'OPEN_VIDEO') {
        // Validated widget→host request: play the cited video full-screen on the
        // HOST page, outside the iframe. The payload is already origin/source/
        // schema-pinned by parseWidgetMessage.
        setVideo(message.payload);
      } else if (message.type === 'OPEN_LINK') {
        // Host-navigation: the widget asked us to open a reference on the host's
        // background surface (validated http(s) url). The support wrapper points
        // its KB iframe at it. No-op on hosts that didn't provide a handler.
        resolved.onHostNavigate?.(message.payload.url);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [mounted, allowedWidgetOrigin, postAuth, resolved.onHostNavigate, close]);

  // The host changed language after the handshake (REQ-028): re-send AUTH so
  // the widget switches. Before READY there is nothing to tell — the first AUTH
  // will carry the current locale.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the locale ALONE on purpose; postAuth's other inputs are delivered by READY.
  useEffect(() => {
    if (widgetReadyRef.current) {
      postAuth();
    }
  }, [resolved.locale]);

  // Close the video lightbox and return focus to the widget iframe.
  const closeVideo = useCallback(() => {
    setVideo(null);
    iframeRef.current?.focus();
  }, []);

  // Lightbox keyboard + focus: Esc closes it (taking precedence over the panel's
  // own Escape-to-close), and focus lands on the lightbox close control on open.
  useEffect(() => {
    if (video === null) {
      return;
    }
    lightboxCloseRef.current?.focus();
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeVideo();
      }
    }
    // Capture phase so we win the Escape before the host-level panel handler.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [video, closeVideo]);

  // Host-level Escape (when focus is on the host page, not inside the iframe).
  useEffect(() => {
    if (!open) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        close();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Move focus into the panel (the iframe) on open.
  useEffect(() => {
    if (open) {
      iframeRef.current?.focus();
    }
  }, [open]);

  const overlayStyle: CSSProperties = { zIndex: resolved.zIndex };

  return (
    <>
      {/* Host-doc launcher styles — tightly scoped `bln-ask-` prefix, no bleed. */}
      <style
        // biome-ignore lint/security/noDangerouslySetInnerHtml: static, first-party stylesheet string (no user input).
        dangerouslySetInnerHTML={{ __html: launcherCss() }}
      />
      <button
        ref={buttonRef}
        type="button"
        className={`bln-ask-btn${compact ? ' bln-ask-btn--compact' : ''}`}
        aria-label={content.askLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...(open ? { 'aria-controls': titleId } : {})}
        onClick={toggle}
      >
        <span aria-hidden="true" className="bln-ask-btn__spark">
          {tokens.sparkle}
        </span>
        {compact ? null : <span className="bln-ask-btn__text">{content.askLabel}</span>}
      </button>

      {mounted ? (
        <div
          className={`bln-ask-overlay bln-ask-overlay--${resolved.placement}${
            open ? ' bln-ask-overlay--open' : ''
          }`}
          style={overlayStyle}
          {...(open ? {} : { 'aria-hidden': 'true' })}
        >
          <iframe
            ref={iframeRef}
            id={titleId}
            className="bln-ask-iframe"
            src={widgetSrc}
            title={content.panelTitle}
            // The widget is a first-party, same-origin/known-origin bundle; keep
            // the sandbox tight but allow scripts + same-origin for the app + the
            // handshake. `allow-popups` (+ `-to-escape-sandbox`) lets citation
            // links / the video article fallback (all `target="_blank"`) open a
            // new tab — without them the sandbox silently blocks every link (FIX
            // A). No top-navigation is granted (links open in a NEW tab, never
            // navigating the host).
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
      ) : null}

      {video !== null ? (
        // Full-viewport video lightbox on the HOST page (Interaction 2) — dark
        // backdrop + a centered 16:9 player embedding ONLY a known provider for
        // the pinned videoId. Close on ×, Esc (captured above), or backdrop click.
        // biome-ignore lint/a11y/useKeyWithClickEvents: the backdrop click is a mouse-only convenience; keyboard users dismiss via Esc (captured above) or the focusable ✕ close button.
        <div
          className="bln-ask-lightbox"
          style={{ zIndex: resolved.zIndex + 1 }}
          role="dialog"
          aria-modal="true"
          aria-label={video.title}
          onClick={(event) => {
            // Backdrop click closes; clicks on the inner stage do not.
            if (event.target === event.currentTarget) {
              closeVideo();
            }
          }}
        >
          <div className="bln-ask-lightbox__stage">
            <button
              ref={lightboxCloseRef}
              type="button"
              className="bln-ask-lightbox__close"
              aria-label={content.closeLabel}
              onClick={closeVideo}
            >
              <span aria-hidden="true">✕</span>
            </button>
            <div className="bln-ask-lightbox__frame">
              <iframe
                className="bln-ask-lightbox__player"
                src={buildPlayerSrc(video)}
                title={video.title}
                // Only our providers are embedded; allow their player features.
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

/**
 * What `mountAskLauncher` returns: calling it tears the launcher down (the
 * original contract, unchanged), and `setLocale` switches the language of a
 * live launcher without remounting it — so an open conversation survives.
 */
export type AskLauncherHandle = (() => void) & {
  setLocale: (locale: string) => void;
};

/**
 * Imperative mount helper for NON-React host surfaces (a `<script>`-injected
 * launcher). Renders `<AskLauncher/>` into `el` and returns a teardown that
 * unmounts it. Uses `react-dom/client` dynamically so the (peer) React DOM is
 * the host's single instance.
 *
 * `setLocale` re-renders the SAME root with a new `locale` prop — React
 * reconciles rather than remounting, so the iframe and its chat are kept.
 */
export function mountAskLauncher(el: HTMLElement, config: LauncherConfig): AskLauncherHandle {
  const root = createRoot(el);
  let current = config;
  root.render(<AskLauncher {...current} />);
  const teardown = () => root.unmount();
  return Object.assign(teardown, {
    setLocale(locale: string) {
      if (locale === current.locale) {
        return;
      }
      current = { ...current, locale };
      root.render(<AskLauncher {...current} />);
    },
  });
}
